import { getBuildPrefix, isLocal } from '@magicschool/utils/nextjs/metadata';
import { isSanitizedErrorLog, sanitizeErrorForLogging } from './sanitizeErrorForLogging';

export type { SanitizedErrorLog } from './sanitizeErrorForLogging';
export { isSanitizedErrorLog, sanitizeErrorForLogging } from './sanitizeErrorForLogging';

type AsyncHooks = typeof import('node:async_hooks');
type AsyncLocalStorageClass = AsyncHooks['AsyncLocalStorage'];
let AsyncLocalStorage: AsyncLocalStorageClass | undefined;

// Runtime-conditional load: bundlers (webpack/turbopack, incl. the Edge Runtime,
// which supports node:async_hooks but not process.getBuiltinModule) provide a
// require that statically resolves the builtin; real Node ESM (tsx prompts CLI)
// has no require, so it falls back to process.getBuiltinModule (destructured so
// Turbopack's edge analysis does not flag the unreachable branch). A static
// node: import is not an option because browser bundles include this module.
if (typeof window === 'undefined') {
  if (typeof require === 'function') {
    AsyncLocalStorage = require('node:async_hooks').AsyncLocalStorage;
  } else if (typeof process !== 'undefined') {
    const { getBuiltinModule } = process;
    AsyncLocalStorage = getBuiltinModule?.('node:async_hooks').AsyncLocalStorage;
  }
}

const loggerStorage: InstanceType<AsyncLocalStorageClass> | null =
  typeof window === 'undefined' && AsyncLocalStorage ? new AsyncLocalStorage<LoggerContext>() : null;

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

// Using JSONStringify will output the content using JSON.stringify which might be what you want but results in unformatted (and untruncated)
export enum OutputStrategy {
  JSONStringify = 'JSONStringify',
  Decorate = 'Decorate',
}

type ILogLevel = {
  level: number;
  type: LogLevel;
  prefix: string;
  color: string;
  bg: string;
};

type ILogUser = {
  id: string;
  email: string;
};

type LoggerContext = {
  path?: string;
  pathKey?: string;
  userId?: string;
  ipAddress?: string;
  build?: string;
};

export class Level implements ILogLevel {
  level: number;
  type: LogLevel;
  prefix: string;
  color: string;
  bg: string;

  constructor(level: number, type: LogLevel, prefix: string, color: string, bg: string) {
    this.level = level;
    this.type = type;
    this.prefix = prefix;
    this.color = color;
    this.bg = bg;
  }
}

// these exist because JavaScript enums are just strings or numbers, really.
export const DEBUG = new Level(10, LogLevel.DEBUG, '🪲 debug 🪲', 'grey', 'white');
export const INFO = new Level(20, LogLevel.INFO, '❗ info ❗', 'black', 'grey');
export const WARN = new Level(40, LogLevel.WARN, '⚠️ warn ⚠️', 'black', 'pink');
export const ERROR = new Level(50, LogLevel.ERROR, '🚑 error 🚑', 'black', 'red');

export class Logger {
  public maxChars = 2000;
  public outputStrategy: OutputStrategy = OutputStrategy.Decorate;

  private get _context(): LoggerContext {
    // In server context, use AsyncLocalStorage
    if (typeof window === 'undefined') {
      return loggerStorage?.getStore() ?? {};
    }

    return {
      path: this._browserPath,
      pathKey: this._browserPathKey,
      userId: this._browserUserId,
      ipAddress: this._browserIpAddress,
      build: this._browserBuild,
    };
  }

  // Browser-only fallback properties (private to encourage using setContext)
  private _browserPath = '';
  private _browserPathKey = '';
  private _browserUserId = '';
  private _browserIpAddress: string | undefined;
  private _browserBuild = '';

  public get path(): string {
    return this._context.path ?? '';
  }

  public set path(value: string) {
    if (typeof window !== 'undefined') {
      this._browserPath = value;
    }
  }

  public get pathKey(): string {
    return this._context.pathKey ?? '';
  }

  public set pathKey(value: string) {
    if (typeof window !== 'undefined') {
      this._browserPathKey = value;
    }
  }

  public get userId(): string {
    return this._context.userId ?? '';
  }

  public set userId(value: string) {
    if (typeof window !== 'undefined') {
      this._browserUserId = value;
    }
  }

  public get ipAddress(): string | undefined {
    return this._context.ipAddress;
  }

  public set ipAddress(value: string | undefined) {
    if (typeof window !== 'undefined') {
      this._browserIpAddress = value;
    }
  }

  public get build(): string {
    return this._context.build ?? '';
  }

  public set build(value: string) {
    if (typeof window !== 'undefined') {
      this._browserBuild = value;
    }
  }

  public runWithContext<T>(context: LoggerContext, fn: () => T): T {
    if (typeof window !== 'undefined' || !loggerStorage) return fn();
    return loggerStorage.run(context, fn);
  }

  public setContext(context: Partial<LoggerContext>): void {
    if (typeof window !== 'undefined' || !loggerStorage) return;
    const currentContext = loggerStorage.getStore() ?? {};
    Object.assign(currentContext, context);
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  debug(msg: string, ...data: any[]): void {
    this._logMessage(DEBUG, msg, data);
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  info(msg: string, ...data: any[]): void {
    this._logMessage(INFO, msg, data);
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  warn(msg: string, ...data: any[]): void {
    this._logMessage(WARN, msg, data);
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  error(msg: string, ...data: any[]): void {
    this._logMessage(ERROR, msg, data);
  }

  private _indent(depth: number): string {
    return '  '.repeat(depth);
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  private _stringify(val: any, maxDisplayLength: number): string {
    let out = val.toString();
    if (typeof val === 'string') {
      out = `'${val}'`;
    }
    // If maxDisplayLength is -1, don't truncate
    if (maxDisplayLength === -1) return out;
    return out.length > maxDisplayLength ? `${out.substr(0, maxDisplayLength)}...'` : out;
  }

  // Helper method to check if an object is an Error instance
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  private _isError(obj: any): obj is Error {
    if (!obj || typeof obj !== 'object') return false;
    // Already-flattened records still have message+stack; re-extracting them would
    // drop `droppedKeyCount` / re-count the sanitizer's own keys.
    if (isSanitizedErrorLog(obj)) return false;
    return obj instanceof Error || ('message' in obj && 'stack' in obj);
  }

  /**
   * Allowlisted primitives only. Never copy nested own-properties — gateway Errors can
   * nest the full BYOK map under request/providerOptions fields.
   */
  private _extractErrorProperties(error: Error) {
    return sanitizeErrorForLogging(error);
  }

  // this turned into a beast. recurse through [object object] and pretty print the output but basically it should turn:
  // {id:'crap', inner:{id:'morecrap'}}
  // into
  // {
  //   id: 'crap',
  //   inner:
  //   {
  //     id: 'morecrap'
  //   }
  // }
  private _objectify(obj: object, maxDepth = 2, currentDepth = 0, maxDisplayLength = -1): string {
    // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
    if (typeof obj !== 'object') return (obj as any).toString();

    // Check if this is an Error instance
    if (this._isError(obj)) {
      const errorProps = this._extractErrorProperties(obj);
      // Don't recursively call _objectify on extracted error properties to avoid infinite recursion
      // Instead, process it as a regular object below
      obj = errorProps;
    }

    let stringified = `${this._indent(currentDepth)}{\n`;
    const keys: string[] = Object.keys(obj);
    for (const [idx, key] of keys.entries()) {
      // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
      const val: any = obj[key as keyof typeof obj];
      if (val && typeof val === 'object' && currentDepth < maxDepth) {
        // Handle nested objects (including Error instances)
        if (Object.keys(val).length || this._isError(val)) {
          stringified += `${this._indent(currentDepth + 1)}${key}:${this._objectify(val, maxDepth, currentDepth + 1, maxDisplayLength)}\n`;
        } else {
          stringified += `${this._indent(currentDepth + 1)}${key}: ${this._stringify(val, maxDisplayLength)}`;
          stringified += idx + 2 <= keys.length ? ',\n' : '\n';
        }
      } else {
        stringified += `${this._indent(currentDepth + 1)}${key}: ${this._stringify(val, maxDisplayLength)}`;
        stringified += idx + 2 <= keys.length ? ',\n' : '\n';
      }
    }
    return `${stringified}${this._indent(currentDepth)}}`;
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  private _arrayify(arr: any[], maxDepth = 2, currentDepth = 0, maxDisplayLength = -1): string {
    let stringed = '[';
    for (const element of arr) {
      if (Array.isArray(element) && currentDepth < maxDepth) {
        stringed += `${this._arrayify(element, maxDepth, currentDepth + 1, maxDisplayLength)}, `;
      } else if (this._isError(element)) {
        // Handle Error instances in arrays
        const errorProps = this._extractErrorProperties(element);
        stringed += `Error ${this._objectify(errorProps, maxDepth, currentDepth + 1, maxDisplayLength)}, `;
      } else {
        stringed += `${this._objectify(element, maxDepth, currentDepth + 1, maxDisplayLength)}, `;
      }
    }
    stringed = stringed.replace(/, $/, ''); // Remove trailing comma and space
    stringed += ']';
    return stringed;
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  private _decorate(data: any[]): string {
    const decorated: string[] = [];
    for (const datum of data) {
      const type = typeof datum;
      switch (type) {
        case 'object':
          if (datum === null) {
            decorated.push('[null]');
          } else if (Array.isArray(datum)) {
            decorated.push(`\n${this._arrayify(datum, 3, 0, this.maxChars)}`);
          } else if (this._isError(datum)) {
            // Handle Error instances specifically to avoid circular references
            const errorProps = this._extractErrorProperties(datum);
            decorated.push(`\nError ${this._objectify(errorProps, 3, 0, this.maxChars)}`);
          } else if (datum.constructor && datum.constructor !== Object) {
            const className = datum.constructor.name;
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            const properties: { [key: string]: any } = {};

            for (const key of Object.getOwnPropertyNames(datum)) {
              properties[key] = datum[key];
            }

            for (const key of Object.getOwnPropertyNames(Object.getPrototypeOf(datum))) {
              if (key !== 'constructor' && typeof datum[key] !== 'function') {
                properties[key] = datum[key];
              }
            }

            decorated.push(`\n${className} ${this._objectify(properties, 3, 0, this.maxChars)}`);
          } else {
            const val = `\n${this._objectify(datum, 3, 0, this.maxChars)}`;
            decorated.push(val);
          }
          break;
        case 'undefined':
          decorated.push('[undefined]');
          break;
        case 'string':
          decorated.push(`'${datum}'`);
          break;
        default:
          decorated.push(datum.toString());
          break;
      }
    }
    return decorated.join(', ');
  }

  private _getFullTimestamp(): string {
    const pad = (n: number, s = 2) => `${new Array(s).fill(0)}${n}`.slice(-s);
    const d = new Date();

    return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
      d.getSeconds(),
    )}.${pad(d.getMilliseconds(), 3)}`;
  }

  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  private _output(content: any): string {
    // Handle empty arrays explicitly
    if (Array.isArray(content) && content.length === 0) {
      return '';
    }

    // Initialize output
    let output = '';

    if (this.outputStrategy === OutputStrategy.Decorate) {
      try {
        // Attempt to decorate the content
        output = this._decorate(content);
      } catch (_e) {
        // Fallback to JSON stringify in case of decoration failure
        output = this._safeStringify(content);
      }
    } else {
      // Default output strategy using JSON stringify
      output = this._safeStringify(content);
    }

    return output;
  }

  // Helper method for safe JSON stringification
  private _safeStringify(content: unknown): string {
    try {
      // Handle Error instances in JSON stringify
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const replacer = (_key: string, value: any) => {
        if (this._isError(value)) {
          return this._extractErrorProperties(value);
        }
        return value;
      };

      return isLocal() ? JSON.stringify(content, replacer, 2) : JSON.stringify(content, replacer);
    } catch (_e) {
      // Fallback to raw content if JSON stringify fails
      return String(content);
    }
  }

  // instead of using console.dir, we write our own so that objects can be output in a single string.
  // biome-ignore lint/suspicious/noExplicitAny: We don't care about the type of val
  private _logMessage(logLevel: ILogLevel, msg: string, data: any[] = []): void {
    const build = this.build || getBuildPrefix();
    const file = Logger.getCallerFilePath() ?? this.pathKey;
    // each time we log, check to see if the log level has been overridden via config setting
    const logLevelForPath = Logger.getPathLogLevel(this.pathKey);
    const stamp = this._getFullTimestamp();

    if (logLevel.level >= logLevelForPath.level) {
      if (typeof window !== 'undefined') {
        const userId = this.userId || '(n/a)';
        console.groupCollapsed(`[${stamp}] [${logLevel.prefix}] [${file}] ${msg}`);
        if (logLevel.level === DEBUG.level) {
          console.trace(
            `%c[${stamp}] ${logLevel.prefix} - ${file}, user-id: ${userId}, build: ${build}.`,
            `background: ${logLevel.bg}; color: ${logLevel.color};`,
            msg,
            this._output(data),
          );
        } else {
          console[logLevel.type](
            `%c[${stamp}] ${logLevel.prefix} - ${file}, user-id: ${userId}, build: ${build}.`,
            `background: ${logLevel.bg}; color: ${logLevel.color};`,
            msg,
            this._output(data),
          );
        }
        console.groupEnd();
      } else {
        const userId = this.userId || '(n/a)';
        const ipAddress = this.ipAddress || '(no-ip)';
        console[logLevel.type](
          `[${stamp}::${ipAddress}] ${logLevel.prefix} - ${file}, user-id: ${userId}, build: ${build}.`,
          msg,
          this._output(data),
        );
      }
    }
  }

  setLogUser = (user: ILogUser | null) => {
    const newUserId = user?.id || '';
    if (typeof window === 'undefined') {
      this.setContext({ userId: newUserId });
    } else {
      this._browserUserId = newUserId;
    }
  };

  // The following code grabs the types of loggers that are available and then checks environment variables to see if there are
  // path-specific log levels defined. They are comma separated strings that contain either url slugs, next pathnames, or router.asPath values
  private static levels = { DEBUG, INFO, WARN, ERROR };
  private static environmentClientLogLevel = process.env.NEXT_PUBLIC_LOGGING_CLIENT_DEFAULT_THRESHOLD as keyof typeof LogLevel;
  private static environmentServerLogLevel = process.env.NEXT_PUBLIC_LOGGING_SERVER_DEFAULT_THRESHOLD as keyof typeof LogLevel;
  static DEFAULT_CLIENT_LEVEL = Logger.levels[Logger.environmentClientLogLevel] || WARN;
  static DEFAULT_SERVER_LEVEL = Logger.levels[Logger.environmentServerLogLevel] || WARN;
  // Once we have any potential overrides, build up a map and convert the string to a Level so we get fancy formatting and all that
  private static pathOverrides: Record<string, Level> = {};
  static {
    if (process.env.NEXT_PUBLIC_LOGGING_PATH_THRESHOLD_DEBUG_OVERRIDE) {
      const debugOverrides = process.env.NEXT_PUBLIC_LOGGING_PATH_THRESHOLD_DEBUG_OVERRIDE?.split(',') ?? [];
      for (const pathname of debugOverrides) {
        Logger.pathOverrides[pathname.trim()] = DEBUG;
      }
    }

    if (process.env.NEXT_PUBLIC_LOGGING_PATH_THRESHOLD_INFO_OVERRIDE) {
      const infoOverrides = process.env.NEXT_PUBLIC_LOGGING_PATH_THRESHOLD_INFO_OVERRIDE?.split(',') ?? [];
      for (const pathname of infoOverrides) {
        Logger.pathOverrides[pathname.trim()] = INFO;
      }
    }
  }

  // This takes the current path and checks it against our map of overrides. If nothing exists, right now we default to the server level default
  // Todo: let it fallback to a client OR a server level
  static getPathLogLevel = (pathname: string): Level => {
    return Logger.pathOverrides[pathname] ?? (typeof window === 'undefined' ? Logger.DEFAULT_SERVER_LEVEL : Logger.DEFAULT_CLIENT_LEVEL);
  };

  private static _traceLineExtractor =
    /at\s+(\w+|.*\$1)\s+\((?:webpack-internal:\/\/\/\([^)]+\)\/|webpack-internal:\/\/\/)?\.\/(src\/(?!util\/Logger)[^:]+):\d+:\d+\)/;

  private static _findFirstMatchingLine(logLines: string[]): { word: string; path: string } | null {
    for (const line of logLines) {
      const match = line.match(Logger._traceLineExtractor);
      if (match) {
        return {
          word: match[1]?.replace('Object.', '')?.replace('$1', '') || '',
          path: match[2] || '',
        };
      }
    }
    return null;
  }

  static getCallerFilePath = () => {
    try {
      const stacktrace = new Error().stack;
      const traces = stacktrace?.split('\n') ?? [];
      const match = Logger._findFirstMatchingLine(traces);
      if (!match) return null;

      const { word, path } = match;
      if (['eval', 'handler'].includes(word)) {
        return `[${path}]`;
      }

      return `${word}[${path}]`;
    } catch {
      // Silently fail
    }
    return null;
  };
}

export const logger = new Logger();
